import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Table, Card, Tag, Spin } from "antd";
import { TrophyOutlined, PhoneOutlined, MailOutlined, LinkedinOutlined } from "@ant-design/icons";
import { GET_SALES_LEADERBOARD } from "../../../Queries/Leads";

const SalesLeaderboard = ({ visible, onClose }) => {
  const { data, loading, refetch } = useQuery(GET_SALES_LEADERBOARD, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (visible) refetch();
  }, [visible]);

  if (!visible) return null;

  const leaderboard = data?.getSalesLeaderboard?.leaderboard || [];

  const columns = [
    {
      title: "#",
      key: "rank",
      width: 40,
      render: (_, __, index) => {
        if (index === 0) return <TrophyOutlined style={{ color: "#faad14", fontSize: 16 }} />;
        if (index === 1) return <TrophyOutlined style={{ color: "#8c8c8c", fontSize: 14 }} />;
        if (index === 2) return <TrophyOutlined style={{ color: "#d48806", fontSize: 14 }} />;
        return index + 1;
      },
    },
    {
      title: "Rep",
      dataIndex: "repName",
      key: "repName",
      width: 140,
      render: (name) => <span style={{ fontWeight: 600 }}>{name}</span>,
    },
    {
      title: <><PhoneOutlined /> Calls</>,
      dataIndex: "calls",
      key: "calls",
      width: 70,
      sorter: (a, b) => a.calls - b.calls,
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: <><MailOutlined /> Emails</>,
      dataIndex: "emails",
      key: "emails",
      width: 70,
      sorter: (a, b) => a.emails - b.emails,
      render: (v) => <Tag color="cyan">{v}</Tag>,
    },
    {
      title: <><LinkedinOutlined /> LinkedIn</>,
      dataIndex: "linkedIn",
      key: "linkedIn",
      width: 70,
      sorter: (a, b) => a.linkedIn - b.linkedIn,
      render: (v) => <Tag color="geekblue">{v}</Tag>,
    },
    {
      title: "Leads",
      dataIndex: "leadsAssigned",
      key: "leadsAssigned",
      width: 60,
      sorter: (a, b) => a.leadsAssigned - b.leadsAssigned,
    },
    {
      title: "Pipeline",
      dataIndex: "pipelineDeals",
      key: "pipelineDeals",
      width: 70,
      sorter: (a, b) => a.pipelineDeals - b.pipelineDeals,
    },
    {
      title: "Won",
      dataIndex: "closedWon",
      key: "closedWon",
      width: 50,
      sorter: (a, b) => a.closedWon - b.closedWon,
      render: (v) => v > 0 ? <Tag color="success">{v}</Tag> : 0,
    },
    {
      title: "Deal Value",
      dataIndex: "totalDealValue",
      key: "totalDealValue",
      width: 100,
      sorter: (a, b) => parseFloat(a.totalDealValue || 0) - parseFloat(b.totalDealValue || 0),
      render: (v) => {
        const num = parseFloat(v || 0);
        return num > 0 ? (
          <span style={{ fontWeight: 700, color: "#059669" }}>
            ${num.toLocaleString()}
          </span>
        ) : "-";
      },
    },
  ];

  // Summary row
  const totals = leaderboard.reduce(
    (acc, r) => ({
      calls: acc.calls + (r.calls || 0),
      emails: acc.emails + (r.emails || 0),
      linkedIn: acc.linkedIn + (r.linkedIn || 0),
      leads: acc.leads + (r.leadsAssigned || 0),
      pipeline: acc.pipeline + (r.pipelineDeals || 0),
      won: acc.won + (r.closedWon || 0),
      value: acc.value + parseFloat(r.totalDealValue || 0),
    }),
    { calls: 0, emails: 0, linkedIn: 0, leads: 0, pipeline: 0, won: 0, value: 0 }
  );

  return (
    <Card
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Sales Leaderboard</span>
          <button
            onClick={onClose}
            style={{
              border: "1px solid #d9d9d9", background: "#fff", borderRadius: 6,
              padding: "2px 10px", fontSize: 12, cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      }
      bordered
      style={{ marginBottom: 12 }}
      bodyStyle={{ padding: "0" }}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 30 }}><Spin /></div>
      ) : (
        <>
          {/* Summary bar */}
          <div style={{
            display: "flex", gap: 16, padding: "10px 16px", background: "#f9fafb",
            borderBottom: "1px solid #f0f0f0", flexWrap: "wrap", fontSize: 12,
          }}>
            <span><b>{leaderboard.length}</b> reps</span>
            <span><PhoneOutlined /> <b>{totals.calls}</b> calls</span>
            <span><MailOutlined /> <b>{totals.emails}</b> emails</span>
            <span><b>{totals.leads}</b> leads</span>
            <span><b>{totals.pipeline}</b> in pipeline</span>
            <span style={{ color: "#059669" }}><b>{totals.won}</b> won (${totals.value.toLocaleString()})</span>
          </div>
          <Table
            rowKey="repName"
            columns={columns}
            dataSource={leaderboard}
            pagination={false}
            size="small"
            style={{ fontSize: 12 }}
          />
        </>
      )}
    </Card>
  );
};

export default SalesLeaderboard;
